import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import FilterSearch from "../../../components/Filtering/FilterSearch";
import FilterRightBox from "../../../components/Filtering/FilterRightBoxOffering";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import SearchButton from "../../../components/buttons/SearchButton";
import DataTable from "../../../components/DataTable/DataTable";
import CustomButton from "../../../components/buttons/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import CreatePolicy from "./Crud/CreatePolicy";
import Swal from "sweetalert2";

import { Tooltip, Chip } from "@mui/material";
import { useFilteredData } from "../../../hooks/useFilteredData";
import { useTableControls } from "../../../hooks/useTableControls";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { useGetPolicies } from "../../../hooks/policies/useGetPolicy";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { formatDate } from "../../../utils/constants";
import { getTextByLang } from "../../../utils/formUtils";
import { extractIdFromIri } from "../../../utils/extractFromIri";
import { useDeletePolicy } from "../../../hooks/policies/useDeletePolicy";

const layoutByVariant = {
  page: {
    headerWidth: "w90",
    filtersWidth: "w86",
    tableWidth: "w90",
    breadcrumbInnerWidth: "w90",
    createButton: "w90",
    containerClass: "container containerflex",
  },
  embedded: {
    headerWidth: "W100",
    filtersWidth: "W100 plr2",
    tableWidth: "W100",
    breadcrumbInnerWidth: "W100",
    createButton: "W100",
    containerClass: "",
  },
};

const headerByVariant = {
  page: {
    Tag: "h2",
    titleClass: "tittlecatalog",
    titleKey: "mypolicies",
  },
  embedded: {
    Tag: "p",
    titleClass: "tittlecatalog--embedded",
    titleKey: "wizard.steps.policy.selection",
  },
};

export default function PoliciesTable({
  variant = "page",
  showHeader = variant === "page",
  showBreadcrumb = variant === "page",
  titleKey = "mypolicies",
  sectionClassName = "",
  canCreateOverride,
  stepData,
  setStepData,
  stepKey = "step2",
}) {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const { isProvider } = useAuthRoles();
  const layout = layoutByVariant[variant];
  const headerConfig = headerByVariant[variant];
  const { Tag, titleClass, titleKey: resolvedTitleKey } = headerConfig;
  const [openDsDialog, setOpenDsDialog] = useState(false);
  const [loading] = useState(false);

  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const {
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    rowCount,
    orgProvider,
    setPagination,
    setRowCount,
    setTableData,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);

  const canMutate = orgProvider;
  const enableSelection = variant !== "page";

  const handleSelectedRowsChange = useCallback(
    (selectedRows, rowSelection) => {
      if (!enableSelection) return;

      const selectedPolicyIds = Object.keys(rowSelection || {}).filter(
        (k) => rowSelection[k],
      );

      const prevSelectedPolicies = Array.isArray(stepData?.selectedPolicies)
        ? stepData.selectedPolicies
        : [];

      const nextPoliciesById = new Map();

      prevSelectedPolicies.forEach((policy) => {
        const id = policy?.id;
        if (id && selectedPolicyIds.includes(id)) {
          nextPoliciesById.set(id, policy);
        }
      });

      (selectedRows || []).forEach((policy) => {
        const id = policy?.id;
        if (id && selectedPolicyIds.includes(id)) {
          nextPoliciesById.set(id, policy);
        }
      });

      const selectedPolicies = selectedPolicyIds
        .map((id) => nextPoliciesById.get(id))
        .filter(Boolean);

      const publisher = selectedPolicies[0]?.assigner || "";

      setStepData?.(stepKey, {
        configById: selectedPolicyIds,
        selectedPolicies,
        publisher,
        rowSelection: rowSelection || {},
      });
    },
    [enableSelection, setStepData, stepData?.selectedPolicies, stepKey],
  );

  const handleOpenCreate = () => {
    setEditingPolicyId(null);
    setOpenDsDialog(true);
  };

  const { mutateAsync: deletePolicy } = useDeletePolicy({
    callApiWithWorker,
    t,
  });

  const handleDelete = async (row) => {
    if (!canMutate) return;
    try {
      const original = row?.original ?? row;
      const id = extractIdFromIri(original?.id);

      if (!id) return;

      const result = await Swal.fire({
        title: t("wizard.steps.policy.deleteConfirmTitle"),
        text: t("wizard.steps.policy.deleteConfirmText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("wizard.steps.policy.deleteConfirmYes"),
        cancelButtonText: t("wizard.steps.policy.deleteConfirmNo"),
      });

      if (!result.isConfirmed) return;

      await deletePolicy({ id });

      if (enableSelection && setStepData) {
        const prevSelection = stepData?.rowSelection || {};
        const nextSelection = { ...prevSelection };
        Object.keys(nextSelection).forEach((key) => {
          if (key === id || extractIdFromIri(key) === id) {
            delete nextSelection[key];
          }
        });

        const nextIds = Object.keys(nextSelection).filter(
          (k) => nextSelection[k],
        );

        setStepData(stepKey, {
          configById: nextIds,
          rowSelection: nextSelection,
          selectedPolicies: (stepData?.selectedPolicies || []).filter(
            (p) => extractIdFromIri(p?.id) !== id,
          ),
          publisher:
            (stepData?.selectedPolicies || []).filter(
              (p) => extractIdFromIri(p?.id) !== id,
            )[0]?.assigner || "",
        });
      }
    } catch (error) {
      console.error("Error deleting policy:", error);
    }
  };

  const handleOpenEdit = (row) => {
    if (!canMutate) return;
    const id = extractIdFromIri(row.id);
    setEditingPolicyId({ id });
    setOpenDsDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDsDialog(false);
    setEditingPolicyId(null);
  };

  const handleSubmit = async (payload, { mode, initialPolicies } = {}) => {
    if (!canMutate) return;
    if (mode === "edit") {
      console.log("Edit data service payload:", payload, initialPolicies);
    } else {
      console.log("Create data service payload:", payload);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("wizard.steps.policy.table_name"),
        accessorKey: "title",
        meta: { width: "45%" },
        cell: ({ getValue }) => (
          <div className="centered-cell">
            <span className="bold">{getValue()}</span>
          </div>
        ),
      },
      {
        header: t("privateArea.metadataPolicies.rule"),
        accessorKey: "ruleCounts",
        meta: { width: "35%" },

        cell: ({ row }) => {
          const {
            permissions = 0,
            obligations = 0,
            prohibitions = 0,
          } = row.original.ruleCounts || {};

          const total = permissions + obligations + prohibitions;

          const tooltipText = `${t("wizard.steps.policy.table.permissions")}: ${permissions}
 • ${t("wizard.steps.policy.table.obligations")}: ${obligations}
 • ${t("wizard.steps.policy.table.prohibitions")}: ${prohibitions}`;

          return (
            <Tooltip title={tooltipText}>
              <Chip
                size="small"
                label={t("wizard.steps.policy.table.rule", { count: total })}
                variant={total ? "filled" : "outlined"}
              />
            </Tooltip>
          );
        },
      },
      {
        header: t("privateArea.metadataPolicies.lastUpdate"),
        accessorKey: "updated_at",
        meta: { width: "20%" },
        cell: ({ getValue }) => formatDate(getValue()),
      },
    ],
    [t],
  );

  const { data: pageResponse } = useGetPolicies({
    callApiWithWorker,
    language: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const rows = useMemo(() => {
    const content =
      pageResponse?.content?.items ??
      pageResponse?.items ??
      pageResponse?.content ??
      [];

    const list = Array.isArray(content) ? content : [];

    return list.map((item) => {
      const p = item.permissions?.length ?? 0;
      const o = item.obligations?.length ?? 0;
      const pr = item.prohibitions?.length ?? 0;

      return {
        ...item,
        title: getTextByLang(item.title, i18n.language) || "-",
        ruleCounts: {
          permissions: p,
          obligations: o,
          prohibitions: pr,
        },
        assignerShort: extractIdFromIri(item.assigner),
      };
    });
  }, [pageResponse, i18n.language]);

  useEffect(() => {
    setRowCount(pageResponse?.totalElements ?? 0);
  }, [pageResponse?.totalElements, setRowCount]);

  useEffect(() => {
    setTableData(rows);
  }, [rows, setTableData]);

  const filteredData = useFilteredData(rows, searchTerm, filters, columns);

  return (
    <section className={sectionClassName}>
      {showBreadcrumb && (
        <div className="w100 flexbread">
          <div className={`divbreadcrum ${layout.breadcrumbInnerWidth}`}>
            <BreadCrumb />
          </div>
        </div>
      )}

      <div className={layout.containerClass}>
        {showHeader && (
          <div className={`${layout.headerWidth} labeltittle`}>
            <Tag className={titleClass}>
              {t(resolvedTitleKey)}
              {enableSelection && <span className="required">*</span>}
            </Tag>
            {isProvider && (
              <CustomButton onClick={handleOpenCreate} startIcon={<AddIcon />}>
                {t("wizard.steps.policy.create")}
              </CustomButton>
            )}
          </div>
        )}
        <div className={`${layout.filtersWidth} filteractions pt3`}>
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>

        <div>
          <FilterRightBox
            open={open}
            onClose={handleClose}
            onApplyFilters={handleApplyFilters}
          />
        </div>

        <div className={`${layout.tableWidth} bgwhite mtb2`}>
          <div className="plr3 ptb">
            <DataTable
              data={filteredData}
              columns={columns}
              showActions={canMutate}
              onEdit={canMutate ? (row) => handleOpenEdit(row) : undefined}
              onDelete={canMutate ? (row) => handleDelete(row) : undefined}
              initialSorting={[{ id: "updated_at", desc: true }]}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
              {...(enableSelection
                ? {
                    enableRowSelection: true,
                    selectionMode: "multi",
                    getRowId: (row) => row.id,
                    selectedRowIds: stepData?.rowSelection || {},
                    onSelectedRowsChange: handleSelectedRowsChange,
                  }
                : {})}
            />
          </div>
        </div>

        <CreatePolicy
          open={openDsDialog}
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
          loading={loading}
          t={t}
          i18n={i18n}
          policyId={editingPolicyId?.id ?? null}
          servesDataset={[]}
          includeIdentifiersInPayload={true}
          enableDirtyCheck={true}
        />
      </div>
    </section>
  );
}
